"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { DayPicker, type DateRange } from "react-day-picker";
import { formatDate, formatDateRange } from "@/lib/format";
import { GREEK_CITIES_SORTED, GREEK_CITY_REGIONS } from "@/lib/greek-cities";

/**
 * Mobile homepage search — a compact pill that opens a full-screen
 * modal with three accordion cards (Where / When / Who), matching
 * Airbnb's mobile search pattern. Only one card is expanded at a
 * time; the others collapse to a summary row showing the current
 * value. A persistent bottom action bar holds Clear all + Search.
 *
 * The desktop pill stays in SearchBar.tsx; both are rendered on the
 * homepage and the appropriate one is shown via CSS media queries
 * (.search-mobile-trigger is hidden ≥720 px; .search is hidden
 * <720 px).
 */
async function locateAndRedirect(
  router: ReturnType<typeof useRouter>,
  onError: (msg: string) => void,
) {
  if (!("geolocation" in navigator)) {
    onError("Your browser doesn't support geolocation.");
    return;
  }
  if ("permissions" in navigator) {
    try {
      const status = await navigator.permissions.query({
        name: "geolocation" as PermissionName,
      });
      if (status.state === "denied") {
        onError(
          "Location is blocked. Allow it in your browser's site settings (the 🔒 icon next to the URL) and try again.",
        );
        return;
      }
    } catch {
      // ignore — fall through to getCurrentPosition.
    }
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const params = new URLSearchParams();
      params.set("lat", pos.coords.latitude.toFixed(5));
      params.set("lng", pos.coords.longitude.toFixed(5));
      router.push(`/search?${params.toString()}`);
    },
    (err) => {
      if (err.code === 1) {
        onError("You blocked location access. Allow it in browser settings.");
      } else if (err.code === 3) {
        onError("Took too long to find your location. Try again or pick a city.");
      } else {
        onError("Couldn't get your location — pick a city instead.");
      }
    },
    { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
  );
}

const FEATURED_DESTS: { city: string; emoji: string }[] = [
  { city: "Athens", emoji: "🏛" },
  { city: "Mykonos", emoji: "⛵" },
  { city: "Santorini", emoji: "🌅" },
  { city: "Crete", emoji: "🏖" },
  { city: "Thessaloniki", emoji: "🏙" },
  { city: "Naxos", emoji: "🏝" },
  { city: "Corfu", emoji: "🌊" },
  { city: "Rhodes", emoji: "🏰" },
];

type Card = "where" | "when" | "who";

export default function SearchBarMobile({
  defaultCity = "",
  defaultCheckIn,
  defaultCheckOut,
  defaultGuests = 1,
}: {
  defaultCity?: string;
  defaultCheckIn?: string;
  defaultCheckOut?: string;
  defaultGuests?: number;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<Card>("where");

  const [city, setCity] = useState(defaultCity);
  const [checkIn, setCheckIn] = useState(defaultCheckIn ?? "");
  const [checkOut, setCheckOut] = useState(defaultCheckOut ?? "");
  const [adults, setAdults] = useState(Math.max(1, defaultGuests));
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [pets, setPets] = useState(0);
  const [locateError, setLocateError] = useState<string | null>(null);

  // Lock body scroll while the modal is open + close on Esc.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const totalAdultChild = adults + children;
  const whereSummary = city || "I'm flexible";
  const whenSummary = (() => {
    if (checkIn && checkOut) return formatDateRange(checkIn, checkOut);
    if (checkIn) return `${formatDate(new Date(checkIn))} → pick checkout`;
    return "Add dates";
  })();
  const whoSummary = (() => {
    if (totalAdultChild === 0) return "Add guests";
    const bits = [`${totalAdultChild} guest${totalAdultChild === 1 ? "" : "s"}`];
    if (infants > 0) bits.push(`${infants} infant${infants === 1 ? "" : "s"}`);
    if (pets > 0) bits.push(`${pets} pet${pets === 1 ? "" : "s"}`);
    return bits.join(", ");
  })();

  function clearAll() {
    setCity("");
    setCheckIn("");
    setCheckOut("");
    setAdults(1);
    setChildren(0);
    setInfants(0);
    setPets(0);
    setActive("where");
  }

  function submit() {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (checkIn) params.set("checkIn", checkIn);
    if (checkOut) params.set("checkOut", checkOut);
    const g = adults + children;
    if (g > 0) params.set("guests", String(g));
    if (adults > 0) params.set("adults", String(adults));
    if (children > 0) params.set("children", String(children));
    if (infants > 0) params.set("infants", String(infants));
    if (pets > 0) params.set("pets", String(pets));
    setOpen(false);
    router.push(`/search?${params.toString()}`);
  }

  const compactSummary = (() => {
    const parts: string[] = [];
    if (city) parts.push(city);
    else parts.push("Anywhere");
    if (checkIn && checkOut) {
      const fmt = (d: string) =>
        new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
      parts.push(`${fmt(checkIn)} — ${fmt(checkOut)}`);
    } else {
      parts.push("Any week");
    }
    parts.push(totalAdultChild > 0 ? `${totalAdultChild} guest${totalAdultChild === 1 ? "" : "s"}` : "Add guests");
    return parts;
  })();

  // Date-range picker plumbing for the embedded calendar.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const range: DateRange | undefined = (() => {
    const f = checkIn ? new Date(checkIn) : undefined;
    const t = checkOut ? new Date(checkOut) : undefined;
    if (!f && !t) return undefined;
    return { from: f, to: t };
  })();

  return (
    <>
      {/* Compact trigger pill — shown only on mobile via CSS. */}
      <button
        type="button"
        className="search-mobile-trigger"
        onClick={() => {
          setOpen(true);
          setActive("where");
        }}
      >
        <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2}>
          <circle cx={6} cy={6} r={4} />
          <path d="M9 9l4 4" />
        </svg>
        <span className="search-mobile-trigger-text">
          <strong>{compactSummary[0]}</strong>
          <span>
            {compactSummary[1]} · {compactSummary[2]}
          </span>
        </span>
      </button>

      {open ? (
        <div className="search-mobile-modal" role="dialog" aria-modal="true">
          <header className="smm-head">
            <button
              type="button"
              className="smm-close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </header>

          <div className="smm-body">
            {/* ── WHERE ──────────────────────────────── */}
            <section
              className={`smm-card${active === "where" ? " on" : ""}`}
              onClick={() => active !== "where" && setActive("where")}
            >
              {active === "where" ? (
                <>
                  <h2 className="smm-card-title">Where?</h2>
                  <div className="smm-input-wrap">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2}>
                      <circle cx={6} cy={6} r={4} />
                      <path d="M9 9l4 4" />
                    </svg>
                    <input
                      autoFocus
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Search destinations"
                      autoComplete="off"
                      spellCheck={false}
                      className="smm-input"
                    />
                    {city ? (
                      <button
                        type="button"
                        aria-label="Clear"
                        className="smm-input-clear"
                        onClick={() => setCity("")}
                      >
                        ×
                      </button>
                    ) : null}
                  </div>

                  {/* Suggestions: featured + Nearby when empty,
                       filtered when typing. */}
                  <div className="smm-suggest-header">Suggested destinations</div>
                  {(() => {
                    const q = city.trim().toLowerCase();
                    const list = q
                      ? GREEK_CITIES_SORTED.filter((c) => c.toLowerCase().includes(q)).slice(0, 8)
                      : null;
                    if (list == null) {
                      return (
                        <>
                          <button
                            type="button"
                            className="sb-suggest-row"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocateError(null);
                              locateAndRedirect(router, setLocateError);
                            }}
                          >
                            <span className="sb-suggest-thumb sb-suggest-thumb-nearby">
                              <svg width="18" height="18" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.5}>
                                <path d="M12 2L2 6.5l4.5 1.5L8 12z" strokeLinejoin="round" />
                              </svg>
                            </span>
                            <span className="sb-suggest-text">
                              <span className="sb-suggest-main">Nearby</span>
                              <span className="sb-suggest-sub">Find what&apos;s around you</span>
                            </span>
                          </button>
                          {FEATURED_DESTS.map((d) => {
                            const region = GREEK_CITY_REGIONS[d.city];
                            return (
                              <button
                                key={d.city}
                                type="button"
                                className="sb-suggest-row"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCity(d.city);
                                  setActive("when");
                                }}
                              >
                                <span className="sb-suggest-thumb sb-suggest-thumb-emoji">
                                  {d.emoji}
                                </span>
                                <span className="sb-suggest-text">
                                  <span className="sb-suggest-main">{d.city}, Greece</span>
                                  <span className="sb-suggest-sub">
                                    {region ? `For ${region.toLowerCase()}-region stays` : "Greece"}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </>
                      );
                    }
                    if (list.length === 0) {
                      return (
                        <div className="sb-suggest-empty">
                          No matches — press Search to search for &ldquo;{city}&rdquo; anyway.
                        </div>
                      );
                    }
                    return list.map((c) => {
                      const region = GREEK_CITY_REGIONS[c];
                      return (
                        <button
                          key={c}
                          type="button"
                          className="sb-suggest-row"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCity(c);
                            setActive("when");
                          }}
                        >
                          <span className="sb-suggest-thumb sb-suggest-thumb-pin">
                            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.5}>
                              <path d="M7 13s-4-3.5-4-7a4 4 0 018 0c0 3.5-4 7-4 7z" />
                              <circle cx="7" cy="6" r="1.5" />
                            </svg>
                          </span>
                          <span className="sb-suggest-text">
                            <span className="sb-suggest-main">{c}, Greece</span>
                            <span className="sb-suggest-sub">{region ?? "Greece"}</span>
                          </span>
                        </button>
                      );
                    });
                  })()}

                  {locateError ? (
                    <p
                      style={{
                        fontSize: 12.5,
                        color: "var(--warn)",
                        padding: "10px 4px 0",
                      }}
                    >
                      {locateError}
                    </p>
                  ) : null}
                </>
              ) : (
                <CollapsedRow label="Where" value={whereSummary} />
              )}
            </section>

            {/* ── WHEN ──────────────────────────────── */}
            <section
              className={`smm-card${active === "when" ? " on" : ""}`}
              onClick={() => active !== "when" && setActive("when")}
            >
              {active === "when" ? (
                <>
                  <h2 className="smm-card-title">When?</h2>
                  <div className="smm-cal">
                    <DayPicker
                      mode="range"
                      selected={range}
                      onSelect={(r) => {
                        if (!r) {
                          setCheckIn("");
                          setCheckOut("");
                          return;
                        }
                        // Complete range + new click → start fresh.
                        const hadComplete = Boolean(checkIn && checkOut);
                        if (hadComplete && r.from && r.to) {
                          const stateDates = new Set([checkIn, checkOut]);
                          const fromStr = formatDate(r.from);
                          const toStr = formatDate(r.to);
                          const clicked =
                            !stateDates.has(fromStr) ? r.from
                            : !stateDates.has(toStr) ? r.to
                            : null;
                          if (clicked) {
                            setCheckIn(formatDate(clicked));
                            setCheckOut("");
                            return;
                          }
                        }
                        if (r.from && r.to && r.to.getTime() === r.from.getTime()) {
                          setCheckIn(formatDate(r.from));
                          setCheckOut("");
                          return;
                        }
                        if (r.from && r.to && r.to < r.from) {
                          setCheckIn(formatDate(r.to));
                          setCheckOut(formatDate(r.from));
                          return;
                        }
                        setCheckIn(r.from ? formatDate(r.from) : "");
                        setCheckOut(r.to ? formatDate(r.to) : "");
                        if (r.from && r.to) setActive("who");
                      }}
                      disabled={[{ before: today }]}
                      numberOfMonths={1}
                      weekStartsOn={1}
                    />
                  </div>
                </>
              ) : (
                <CollapsedRow label="When" value={whenSummary} />
              )}
            </section>

            {/* ── WHO ───────────────────────────────── */}
            <section
              className={`smm-card${active === "who" ? " on" : ""}`}
              onClick={() => active !== "who" && setActive("who")}
            >
              {active === "who" ? (
                <>
                  <h2 className="smm-card-title">Who?</h2>
                  <div className="sb-who" style={{ position: "static", boxShadow: "none", padding: 0, border: 0, width: "auto", marginTop: 4 }}>
                    <StepperRow
                      title="Adults"
                      sub="Ages 13 or above"
                      value={adults}
                      min={1}
                      onChange={(v) => setAdults(v)}
                    />
                    <StepperRow
                      title="Children"
                      sub="Ages 2 — 12"
                      value={children}
                      min={0}
                      onChange={(v) => setChildren(v)}
                    />
                    <StepperRow
                      title="Infants"
                      sub="Under 2"
                      value={infants}
                      min={0}
                      onChange={(v) => setInfants(v)}
                    />
                    <StepperRow
                      title="Pets"
                      sub="Check the listing's pet policy"
                      value={pets}
                      min={0}
                      onChange={(v) => setPets(v)}
                      last
                    />
                  </div>
                </>
              ) : (
                <CollapsedRow label="Who" value={whoSummary} />
              )}
            </section>
          </div>

          <footer className="smm-foot">
            <button type="button" className="smm-clear" onClick={clearAll}>
              Clear all
            </button>
            <button type="button" className="search-btn smm-search" onClick={submit}>
              <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx={6} cy={6} r={4} />
                <path d="M9 9l4 4" />
              </svg>
              <span>Search</span>
            </button>
          </footer>
        </div>
      ) : null}
    </>
  );
}

function CollapsedRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="smm-collapsed">
      <span className="smm-collapsed-label">{label}</span>
      <span className="smm-collapsed-value">{value}</span>
    </div>
  );
}

function StepperRow({
  title,
  sub,
  value,
  min,
  onChange,
  last,
}: {
  title: string;
  sub: string;
  value: number;
  min: number;
  onChange: (v: number) => void;
  last?: boolean;
}) {
  return (
    <div className={`sb-who-row${last ? " last" : ""}`}>
      <div>
        <div className="sb-who-title">{title}</div>
        <div className="sb-who-sub">{sub}</div>
      </div>
      <div className="sb-who-stepper" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label={`Decrease ${title}`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          −
        </button>
        <span aria-live="polite">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${title}`}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
    </div>
  );
}
